import type { UserPublicID } from "@atoms/atom-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { onServerSessionRejected } from "../auth/serverSessionRejection";
import type { NoAccessDetails } from "../FrontendProvision";
import { getBackendUrl } from "../getBackendUrl";
import { type GetAuthToken, makeGetAuthToken } from "../makeGetAuthToken";
import { useUserStore } from "./useMyUserId";

type UseHandleOidcEventsOptions = Readonly<{
  disableAuthFlows?: boolean;
  /**
   * Receives the token getter of the HTTP and WebSocket links, which renews
   * an expired token through this OIDC provider before it returns it.
   */
  setGetAuthToken?: (getter: GetAuthToken) => void;
  /**
   * Renews the token ahead of its expiry (the project's
   * `automaticSilentRenew`). Without it, the token is renewed when it expired.
   */
  renewOnExpiring?: boolean;
}>;

export type OidcUserResolutionStatus =
  | "idle"
  | "resolving"
  | "resolved"
  | "no-access"
  | "error";

export interface UseHandleOidcEventsResult {
  status: OidcUserResolutionStatus;
  noAccessDetails: NoAccessDetails | null;
  error: Error | null;
}

export const useHandleOidcEvents = (
  options?: UseHandleOidcEventsOptions,
): UseHandleOidcEventsResult => {
  const disableAuthFlows = options?.disableAuthFlows ?? false;
  const setGetAuthToken = options?.setGetAuthToken;
  const renewOnExpiring = options?.renewOnExpiring ?? true;
  const { backendBaseUrl } = getBackendUrl();
  const atomUserEndpoint = `${backendBaseUrl.replace(/\/$/, "")}/auth/me`;
  const setUserId = useUserStore((state) => state.setUserId);
  const {
    isAuthenticated,
    activeNavigator,
    isLoading,
    signinRedirect,
    user,
    events,
    signinSilent,
    error,
    signoutRedirect,
    settings,
  } = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [status, setStatus] = useState<OidcUserResolutionStatus>("idle");
  const [noAccessDetails, setNoAccessDetails] =
    useState<NoAccessDetails | null>(null);
  const [resolutionError, setResolutionError] = useState<Error | null>(null);
  /**
   * Rule: Every asynchronous read that sets auth state takes a generation at
   * its start and applies its answer only while that generation is still
   * current — a sign-out, a published answer, or the start of a read for a
   * changed credential (a new OIDC user) bumps it, while a read of the same
   * credential that gets no answer leaves earlier reads alone — and a
   * trigger that arrives while such a read runs is remembered and runs the
   * read once more when it ends; what neither covers waits for the next
   * periodic server check.
   *
   * The generation of the `/auth/me` lookups (resolution and re-check).
   */
  const lookupGeneration = useRef(0);
  /** The navigator now (sign-in, renewal, sign-out), for running re-checks. */
  const navigatorRef = useRef(activeNavigator);
  navigatorRef.current = activeNavigator;

  // One getter for the links, the expired event and the re-check, so that
  // they all share one renewal. A bootstrap user is never renewed.
  const tokenGetter = useMemo(
    () =>
      makeGetAuthToken(
        settings.authority,
        settings.client_id,
        disableAuthFlows
          ? undefined
          : {
              signinSilent,
              getNavigator: () => navigatorRef.current,
            },
      ),
    [settings.authority, settings.client_id, disableAuthFlows, signinSilent],
  );
  useEffect(() => {
    setGetAuthToken?.(tokenGetter);
  }, [setGetAuthToken, tokenGetter]);
  // The re-check and the expired event share the whole run, so that a failed
  // renewal starts one sign-in redirect, not one for each of them.
  const renewOrSignInRun = useRef<Promise<void> | null>(null);
  const renewOrSignInOnce = useCallback(
    (isCurrent: () => boolean): Promise<void> => {
      renewOrSignInRun.current ??= renewOrSignIn(
        tokenGetter.renew,
        signinRedirect,
        isCurrent,
      ).finally(() => {
        renewOrSignInRun.current = null;
      });
      return renewOrSignInRun.current;
    },
    [tokenGetter, signinRedirect],
  );

  // Update user ID when authenticated using backend /auth/me.
  useEffect(() => {
    let cancelled = false;
    const syncUserId = async () => {
      if (!isAuthenticated || !user) {
        if (!cancelled) {
          setUserId(null);
          setStatus("idle");
          setNoAccessDetails(null);
          setResolutionError(null);
        }
        return;
      }

      if (!cancelled) {
        setStatus("resolving");
        setNoAccessDetails(null);
        setResolutionError(null);
      }

      const accessToken =
        (user as { access_token?: string }).access_token ?? undefined;
      if (!accessToken) {
        if (!cancelled) {
          setUserId(null);
          setStatus("no-access");
          setNoAccessDetails({ reason: "missing-access-token" });
        }
        return;
      }

      lookupGeneration.current++;
      const generation = lookupGeneration.current;
      const lookup = await lookUpAtomUser(atomUserEndpoint, accessToken);
      if (cancelled || generation !== lookupGeneration.current) return;
      switch (lookup.kind) {
        case "resolved":
          if (lookup.userId) {
            setUserId(lookup.userId);
            setStatus("resolved");
          } else {
            setUserId(null);
            setStatus("no-access");
            setNoAccessDetails({
              reason: "missing-local-user-mapping",
              detail:
                "No local user mapping exists for the authenticated account.",
            });
          }
          return;
        case "rejected":
          setUserId(null);
          setStatus("no-access");
          setNoAccessDetails(noAccessOf(lookup));
          return;
        case "failed":
          setUserId(null);
          setStatus("error");
          setResolutionError(lookup.error);
      }
    };

    void syncUserId();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, setUserId, atomUserEndpoint]);

  // The server ended a connection whose credential it no longer accepts
  // (WebSocket close 4403). Ask the backend first: a user it refuses with a
  // token that is still valid (banned, no access) gets the no-access screen
  // at once; only an expired token is renewed, and only a failed renewal
  // sends the user to the sign-in of the identity provider. Without auth
  // flows (a bootstrap user) nothing is renewed: a refused user gets the
  // no-access screen.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let active = true;
    let running = false;
    /** A rejection arrived while a re-check ran: run it once more. */
    let pending = false;
    const controller = new AbortController();
    const recheck = async () => {
      if (running) {
        pending = true;
        return;
      }
      running = true;
      // A lookup that hangs must not block the next rejection for good.
      const lookupAbort = new AbortController();
      const abortLookup = () => lookupAbort.abort();
      controller.signal.addEventListener("abort", abortLookup);
      const timeout = setTimeout(abortLookup, RECHECK_TIMEOUT_MS);
      // Only a re-check that publishes a result makes earlier lookups stale;
      // one that finds the user accepted leaves a pending resolution alone.
      const generation = lookupGeneration.current;
      try {
        const accessToken = (user as { access_token?: string }).access_token;
        const lookup = accessToken
          ? await lookUpAtomUser(
              atomUserEndpoint,
              accessToken,
              lookupAbort.signal,
            )
          : ({ kind: "rejected", status: 401 } as const);
        clearTimeout(timeout);
        if (!active || generation !== lookupGeneration.current) return;
        if (lookup.kind !== "rejected") return;
        if (
          !disableAuthFlows &&
          lookup.status === 401 &&
          (!accessToken || user.expired)
        ) {
          // A sign-in or sign-out under way is not interrupted. The
          // navigator is read from a ref: this re-check's own silent
          // renewal sets it too and must not end the re-check.
          if (navigatorRef.current) return;
          await renewOrSignInOnce(
            () => active && !navigatorRef.current?.startsWith("signout"),
          );
          return;
        }
        lookupGeneration.current++;
        setUserId(null);
        setStatus("no-access");
        setNoAccessDetails(noAccessOf(lookup));
      } catch (e) {
        // The sign-in redirect could not start: show the error screen.
        if (active) {
          lookupGeneration.current++;
          setUserId(null);
          setStatus("error");
          setResolutionError(
            e instanceof Error ? e : new Error("Sign-in could not start."),
          );
        }
      } finally {
        clearTimeout(timeout);
        controller.signal.removeEventListener("abort", abortLookup);
        running = false;
        if (pending && active) {
          pending = false;
          void recheck();
        }
      }
    };
    const unregister = onServerSessionRejected(() => {
      void recheck();
    });
    return () => {
      active = false;
      controller.abort();
      unregister();
    };
  }, [
    disableAuthFlows,
    isAuthenticated,
    user,
    atomUserEndpoint,
    setUserId,
    renewOrSignInOnce,
  ]);

  // automatically sign-in
  useEffect(() => {
    const signIn = async () => {
      if (
        !disableAuthFlows &&
        !hasAuthParams() &&
        !isAuthenticated &&
        !activeNavigator &&
        !isLoading &&
        !hasTriedSignin &&
        !error
      ) {
        try {
          await signinRedirect();
        } catch (e) {
          console.error("Auto Sign-in failed", e);
        } finally {
          setHasTriedSignin(true);
        }
      }
    };

    void signIn();
  }, [
    disableAuthFlows,
    hasTriedSignin,
    isAuthenticated,
    activeNavigator,
    isLoading,
    signinRedirect,
    error,
  ]);

  // The framework is the one owner of silent renewal; the automatic renewal
  // of oidc-client-ts is off (AuthProvider). The expired event (also raised
  // when a sleeping tab wakes up) renews the token silently and signs in again
  // only when that fails. A failed renewal ahead of the expiry is tried once
  // more, as the library's renewal does after a timeout.
  useEffect(() => {
    if (disableAuthFlows) {
      return;
    }
    let active = true;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const cancelRetry = () => clearTimeout(retry);
    const retryRenewal = () => {
      void tokenGetter.renew();
    };
    const removeAccessTokenExpiring = renewOnExpiring
      ? events.addAccessTokenExpiring(async () => {
          const renewed = await tokenGetter.renew();
          if (!renewed && active) {
            retry = setTimeout(retryRenewal, EXPIRING_RETRY_MS);
          }
        })
      : undefined;
    const removeUserLoaded = events.addUserLoaded(cancelRetry);
    const removeAccessTokenExpired = events.addAccessTokenExpired(() => {
      console.debug("access token expired");
      // A sign-in or sign-out under way is not interrupted; a silent
      // renewal under way is joined.
      const underWay = navigatorRef.current;
      if (underWay && underWay !== "signinSilent") return;
      renewOrSignInOnce(
        () => active && !navigatorRef.current?.startsWith("signout"),
      ).catch((e: unknown) => {
        console.error("Sign-in after the token expired failed", e);
      });
    });

    return () => {
      active = false;
      cancelRetry();
      removeAccessTokenExpiring?.();
      removeAccessTokenExpired();
      removeUserLoaded();
    };
  }, [
    events,
    tokenGetter,
    renewOrSignInOnce,
    disableAuthFlows,
    renewOnExpiring,
  ]);

  useEffect(() => {
    if (error) {
      if (error.message.includes("No matching state found in storage")) {
        // some users may have a bookmark with an invalid state parameter
        // this causes an error, so we need to sign out and redirect to the login page
        // to definitely get a new + valid state parameter
        void signoutRedirect({
          post_logout_redirect_uri: globalThis.location.origin,
        });
      }
      console.error("oidc error", error);
    }
  }, [error, signoutRedirect]);

  return {
    status,
    noAccessDetails,
    error: resolutionError,
  };
};

/** How long a re-check after a rejected session waits for `/auth/me`. */
const RECHECK_TIMEOUT_MS = 15_000;

/** The pause before a failed renewal ahead of the expiry is tried again. */
const EXPIRING_RETRY_MS = 5_000;

type AtomUserLookup =
  | { kind: "resolved"; userId: UserPublicID | null }
  | { kind: "rejected"; status: 401 | 403; detail?: string }
  | { kind: "failed"; error: Error };

/** Asks the backend (`/auth/me`) which app user the access token belongs to. */
async function lookUpAtomUser(
  endpoint: string,
  accessToken: string,
  signal?: AbortSignal,
): Promise<AtomUserLookup> {
  try {
    const response = await fetch(endpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) {
      const detail = await readResponseDetail(response);
      if (response.status === 401 || response.status === 403) {
        return { kind: "rejected", status: response.status, detail };
      }
      return {
        kind: "failed",
        error: new Error(
          `Failed to resolve app user id from backend (status ${response.status}).`,
        ),
      };
    }
    const data = (await response.json()) as { userId?: string };
    return {
      kind: "resolved",
      userId: (data.userId as UserPublicID | undefined) ?? null,
    };
  } catch (e) {
    console.error("Failed to resolve atom user id from backend", e);
    return {
      kind: "failed",
      error:
        e instanceof Error
          ? e
          : new Error("Unknown error while resolving app user id."),
    };
  }
}

function noAccessOf(
  lookup: Extract<AtomUserLookup, { kind: "rejected" }>,
): NoAccessDetails {
  return {
    reason: lookup.status === 403 ? "forbidden" : "access-denied",
    statusCode: lookup.status,
    detail: lookup.detail,
  };
}

/** Renews the token silently; a failed renewal signs in again. */
async function renewOrSignIn(
  renew: () => Promise<unknown>,
  signinRedirect: () => Promise<void>,
  isCurrent: () => boolean,
): Promise<void> {
  const renewed = await renew();
  // Not for a session that ended or changed meanwhile.
  if (!renewed && isCurrent()) {
    await signinRedirect();
  }
}

async function readResponseDetail(
  response: Response,
): Promise<string | undefined> {
  try {
    const bodyText = (await response.text()).trim();
    if (!bodyText) {
      return undefined;
    }
    return bodyText;
  } catch {
    return undefined;
  }
}
